import { Box, Button } from '@mui/material';

import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import type { RailProgressState } from './IntakeShell';
import { cardSx, chipSx, wf } from './tokens';

export interface ReviewSubmitItem {
  pageKey: string;
  label: string;
  state: RailProgressState;
}

interface IntakeReviewSubmitPageProps {
  sectionLabel: string;
  items: ReviewSubmitItem[];
  onNavigate: (pageKey: string) => void;
  onContinue: () => void;
  onBack: () => void;
  nextLabel: string;
}

const STATUS_LABELS: Record<RailProgressState, string> = {
  completed: 'Complete',
  inProgress: 'In progress',
  notStarted: 'Not started',
};

export function IntakeReviewSubmitPage({
  sectionLabel,
  items,
  onNavigate,
  onContinue,
  onBack,
  nextLabel,
}: IntakeReviewSubmitPageProps) {
  const incompleteCount = items.filter(item => item.state !== 'completed').length;
  const isComplete = items.length > 0 && incompleteCount === 0;
  const statusTitle = isComplete
    ? `${sectionLabel} is complete`
    : `${incompleteCount} section${incompleteCount === 1 ? '' : 's'} left to complete`;

  return (
    <>
      <IntakePageHeader
        title="Review & submit"
        sub={`Review your ${sectionLabel.toLowerCase()} before moving on. You can return to any section to make changes.`}
      />

      <Box
        role="status"
        sx={{
          ...cardSx,
          background: isComplete ? wf.greenSoft : wf.amberSoft,
          borderColor: isComplete ? wf.green : wf.amber,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{ color: isComplete ? wf.green : wf.amber, fontSize: '18px', fontWeight: 700 }}
        >
          {isComplete ? '✓' : '!'}
        </Box>
        <Box>
          <Box sx={{ color: wf.ink, fontSize: '14px', fontWeight: 700 }}>{statusTitle}</Box>
          <Box sx={{ color: wf.muted, fontSize: '12px', mt: '2px' }}>
            {isComplete
              ? 'All required information has been saved.'
              : 'Finish the remaining work before continuing.'}
          </Box>
        </Box>
      </Box>

      <Box component="section" aria-label={`${sectionLabel} review`} sx={cardSx}>
        {items.map((item, index) => {
          const complete = item.state === 'completed';
          return (
            <Box
              key={item.pageKey}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '46px',
                borderTop: index === 0 ? 'none' : `1px solid ${wf.lineSoft}`,
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: complete ? wf.greenSoft : wf.surfaceAlt,
                  color: complete ? wf.green : wf.muted,
                  border: `1px solid ${complete ? wf.green : wf.line}`,
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {complete ? '✓' : '•'}
              </Box>
              <Box sx={{ color: wf.ink, fontSize: '13px', fontWeight: 600 }}>{item.label}</Box>
              <Box sx={chipSx(complete ? 'ok' : item.state === 'inProgress' ? 'pend' : 'mut')}>
                {STATUS_LABELS[item.state]}
              </Box>
              <Button
                aria-label={`Edit ${item.label}`}
                onClick={() => onNavigate(item.pageKey)}
                size="small"
                variant="text"
                sx={{ ml: 'auto', textTransform: 'none', fontSize: '12px' }}
              >
                Edit
              </Button>
            </Box>
          );
        })}
      </Box>

      <IntakePageFooter
        primaryLabel={nextLabel}
        onPrimary={onContinue}
        onBack={onBack}
        disabled={!isComplete}
        disabledReason={
          isComplete ? undefined : 'Complete the remaining sections before continuing.'
        }
      />
    </>
  );
}
